import { useCallback, useMemo } from "react"
import "react-toastify/dist/ReactToastify.css"
import { toast, ToastOptions, ToastPosition } from "react-toastify"
import type { Id } from "react-toastify"

export const useMessage = () => {
	const baseToastOptions = useMemo<ToastOptions>(
		() => ({
			position: "bottom-left" as ToastPosition,
			autoClose: 4000,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
		}),
		[],
	)

	const notify = useCallback(
		(text: string, options?: ToastOptions) => {
			if (text) {
				toast(text, { ...baseToastOptions, ...options })
			}
		},
		[baseToastOptions],
	)

	const success = useCallback(
		(text: string) => {
			notify(text, { type: "success" })
		},
		[notify],
	)

	const error = useCallback(
		(text: unknown) => {
			if (typeof text === "string") {
				notify(text, { type: "error" })
			} else if (text instanceof Error) {
				notify(text.message, { type: "error" })
			} else {
				notify("Error", { type: "error" })
			}
		},
		[notify],
	)

	// Spinner toast for work that takes long enough to need acknowledging but should not block the
	// UI. It stays up until `resolve` (or `dismiss`) is called with the returned id, so the outcome
	// survives the trigger disappearing — a menu closing on click, a drawer being closed.
	const loading = useCallback(
		(text: string): Id => toast.loading(text, { ...baseToastOptions, autoClose: false, closeOnClick: false }),
		[baseToastOptions],
	)

	// Turns a `loading` toast into its outcome in place, so one row appears, changes, then
	// auto-closes — instead of a spinner and a result stacking up as two separate toasts. Anything
	// other than a plain success stays up longer, since it has to be read to be acted on.
	const resolve = useCallback(
		(id: Id, text: string, type: "success" | "error" | "warning") => {
			toast.update(id, {
				...baseToastOptions,
				render: text,
				type,
				isLoading: false,
				closeOnClick: true,
				autoClose: type === "success" ? 4000 : 6000,
			})
		},
		[baseToastOptions],
	)

	// Switches an existing toast (typically one opened by `loading`) into a controlled, determinate
	// progress bar — for work whose remaining amount IS measurable (bytes downloaded), unlike the
	// indeterminate spinner `loading` shows. `autoClose: false` is load-bearing: react-toastify drives
	// its progress bar off the auto-close countdown whenever one is set, which would silently override
	// whatever fraction is passed in here. `fraction` is react-toastify's own `progress` unit (0-1).
	//
	// `fraction` is optional so a caller can announce a new phase (e.g. "Downloading…") before it has
	// a real number to show — a browser progress event only reports a fraction once it can compute a
	// total, so this keeps the toast on the indeterminate spinner rather than showing a bar stuck at 0%
	// for however long that takes (#1401 review).
	const updateProgress = useCallback(
		(id: Id, fraction: number | undefined, text?: string) => {
			toast.update(id, {
				...baseToastOptions,
				...(text !== undefined ? { render: text } : {}),
				type: "default",
				closeOnClick: false,
				autoClose: false,
				isLoading: fraction === undefined,
				progress: fraction === undefined ? undefined : Math.min(Math.max(fraction, 0), 1),
			})
		},
		[baseToastOptions],
	)

	return { notify, success, error, loading, resolve, updateProgress }
}
